import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section, CTA, DotCircle } from '../components/Pieces';
import { ecceStats, ecceOutcomes, ecceDeployments, eccePillars, siteImages } from '../mock';
import { Sparkles, Users, Heart, BarChart3, Building2, Award, Baby, Radio, Cloud, Monitor, Brain, Smartphone } from 'lucide-react';

const outcomeIcon = (a) => ({ CHILDREN: Baby, WORKER: Users, COMMUNITY: Heart, WCDD: BarChart3, GOVERNMENT: Building2, 'CSR DONOR': Award }[a] || Sparkles);
const pillarIcon = (i) => [Radio, Monitor, Cloud, BarChart3, Brain, Smartphone][i] || Sparkles;

export default function SmartECCE() {
  return (
    <Shell>
      <Seo title="Smart ECCE / Digital Anganwadi"
        description="Aadharshila-aligned foundational learning delivered daily: device, connectivity, curriculum content, worker enablement and monitoring for Anganwadi centres."
        image={siteImages.smartEcce} />
      <PageHero
        kicker="Digital Anganwadi & Smart ECCE"
        title="Joyful foundational learning. Consistent delivery. Evidence from every classroom."
        sub="Device + Connectivity + Curriculum-aligned Content + Worker Enablement + Classroom Delivery + Monitoring — designed for the reality of grassroots infrastructure."
        breadcrumbs={[{ label: 'Transform Education' }, { label: 'Smart ECCE' }]}
        image={siteImages.smartEcce}
        imageAlt="An Anganwadi worker leading an interactive lesson as young children raise their hands, watching animated learning content projected by a Doot device onto the classroom wall"
      />

      <Section bg="white">
        <div className="grid md:grid-cols-3 gap-5">
          {ecceStats.map((s) => (
            <div key={s.value} className="mm-card p-8 text-center">
              <div className="font-display text-5xl md:text-6xl font-bold text-[var(--mm-purple)]">{s.value}</div>
              <div className="mt-3 font-semibold text-[var(--mm-ink)]">{s.label}</div>
              <div className="mt-1 text-sm text-[var(--mm-ink)]/60">{s.sub}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-[var(--mm-ink)]/50 text-center">Source: Mashmari Digital Anganwadi National Scale brochure, Sep 2026.</p>
      </Section>

      <Section bg="cream2">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mm-kicker mb-3">Six Ways the Model Delivers</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">Proven outcomes, for every stakeholder.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ecceOutcomes.map((o) => {
            const Icon = outcomeIcon(o.audience);
            return (
              <div key={o.audience} className="mm-card p-6">
                <div className="flex items-center gap-4 mb-4">
                  <DotCircle size={68}><Icon className="w-5 h-5"/></DotCircle>
                  <span className="mm-kicker">{o.audience}</span>
                </div>
                <h3 className="font-display text-lg font-bold text-[var(--mm-ink)]">{o.title}</h3>
                <p className="mt-2 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{o.body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section bg="white">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mm-kicker mb-3">One Model, Three Scales</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">From a single village to a 67-centre pilot.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {ecceDeployments.map((d) => (
            <div key={d.location} className="mm-card p-6">
              <div className="mm-kicker mb-3">{d.scale}</div>
              <h3 className="font-display text-xl font-bold text-[var(--mm-ink)]">{d.location}</h3>
              <p className="mt-3 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{d.body}</p>
              {d.evidence && <div className="mt-4 pt-4 border-t border-[var(--mm-line)] text-xs text-[var(--mm-purple)] font-semibold">📄 {d.evidence}</div>}
            </div>
          ))}
        </div>
      </Section>

      <Section bg="cream2">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mm-kicker mb-3">What the Model Delivers</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[var(--mm-ink)] mm-rule-center">Six pillars of the Digital ECCE System.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {eccePillars.map((p, i) => {
            const Icon = pillarIcon(i);
            return (
              <div key={p.title} className="mm-card p-6">
                <div className="w-12 h-12 rounded-full bg-[var(--mm-purple)] text-white flex items-center justify-center mb-4"><Icon className="w-5 h-5"/></div>
                <h3 className="font-display text-lg font-bold text-[var(--mm-ink)]">{p.title}</h3>
                <p className="mt-2 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <CTA title="Fund a classroom that works every single day." sub="Government-mandated CSR theme for FY 2026-27 and 2027-28. Ready for national scale." primary={{ label: 'Partner with us', href: '/contact' }} />
    </Shell>
  );
}
